import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from 'src/i18n';
import { sortedByName } from 'src/store/utilities.js';
import type { User } from 'src/store/types.js';
import './d-me-organizations.js';

/**
 * Me page for managing user organizations
 * Shows list of organizations with Leave and Delete options
 * Test page - no links to this page from other parts of the system
 *
 * @fires request-logout
 * @fires leave-organization
 * @fires delete-organization
 */
@customElement('d-me')
export class DMe extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      min-height: 100vh;
      background: var(--outskirts-background-color);
      padding-top: 0px;
    }

    .outskirts {
      margin: 0 auto;
      height: 100vh;
      overflow: auto;
    }

    .outskirts d-application-header {
      margin: 0 auto;
      width: 768px;
      padding-left: 20px;
      padding-right: 20px;
      max-width: var(--appWidth);
      min-width: 360px;
      box-sizing: border-box;
    }

    .outskirtsHeader {
      margin: 0 auto;
      padding: 0 20px;
      width: 768px;
      max-width: var(--appWidth);
      min-width: 360px;
      box-sizing: border-box;
    }

    .outskirtsHeader > div {
      display: flex;
      justify-content: space-between;
      position: relative;
      color: white;
      height: var(--applicationHeaderHeight);
      box-sizing: border-box;
      border-bottom: 1px solid hsla(0, 0%, 100%, 0.2);
    }

    .outskirtsHeader > div > div {
      display: flex;
    }

    .outskirtsHeader .logo {
      flex: none;
      width: 137px;
      height: 45px;
      margin-bottom: -5px;
      background: url(/images/trinnvis-logo-white.svg) -2px 10px no-repeat;
      background-size: 97px 25px;
      text-indent: -10000px;
    }

    .outskirtsHeader a {
      font-size: 13px;
      padding: 16px 20px 0 16px;
      height: 100%;
      opacity: 0.7;
      box-sizing: border-box;
      text-decoration: none;
      cursor: pointer;
      color: white;
    }

    .outskirtsHeader a:hover {
      opacity: 1;
    }

    .outskirtsHeader a:last-child {
      margin-right: -20px;
    }

    .outskirts a {
      color: white;
    }

    .outskirts h1 {
      font-size: 24px;
      font-weight: 500;
      line-height: 120%;
      color: white;
      margin-bottom: 20px;
    }

    .outskirtsContent {
      padding-top: 20px;
      padding-bottom: 20px;
    }

    .outskirtsContent {
      margin: 0 auto;
      width: 768px;
      padding-left: 20px;
      padding-right: 20px;
      max-width: var(--appWidth);
      min-width: 360px;
      box-sizing: border-box;
    }

    .subtitle {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.7);
      margin-bottom: 20px;
    }

    .info-box {
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      padding: 12px;
      margin-bottom: 20px;
      color: white;
      font-size: 14px;
    }

    .info-box strong {
      color: #ffc107;
    }

    d-me-organizations:after {
      content: '';
      display: block;
      height: 170px;
    }
  `;

  @property({ type: Object })
  user!: User;

  render() {
    return html`
      <div class="outskirts">
        <div class="outskirtsHeader">
          <div>
            <div class="logo">Trinnvis</div>
            <div>
              <a href="/account">${t('account.backToAccount')}</a>
              ${!this.user
                ? nothing
                : html`<a href="/" @click=${() => this.handleLogout()}>${t('account.accountSecured.logout')}</a>`}
            </div>
          </div>
        </div>
        <div class="outskirtsContent">
          <h1>${t('account.accountSecured.organizations')}</h1>

          <d-me-organizations
            id="meOrganizations"
            .organizations=${sortedByName(this.user.organizations ?? [])}
            .user=${this.user}
            @leave-organization=${this.handleLeaveOrganization}
            @delete-organization=${this.handleDeleteOrganization}
          >
          </d-me-organizations>
        </div>
      </div>
    `;
  }

  private handleLogout() {
    this.dispatchEvent(
      new CustomEvent('request-logout', {
        bubbles: true,
        composed: true,
        detail: { redirect: true },
      }),
    );
  }

  private async handleLeaveOrganization(e: CustomEvent<{ organizationId: number }>) {
    const { organizationId } = e.detail;

    // This will call the API to leave the organization
    this.dispatchEvent(
      new CustomEvent('api-leave-organization', {
        bubbles: true,
        composed: true,
        detail: { organizationId },
      }),
    );
  }

  private async handleDeleteOrganization(e: CustomEvent<{ organizationId: number }>) {
    const { organizationId } = e.detail;

    // This will call the API to delete the organization
    this.dispatchEvent(
      new CustomEvent('api-delete-organization', {
        bubbles: true,
        composed: true,
        detail: { organizationId },
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-me': DMe;
  }
}
